import React, { useEffect, useState } from 'react';
import ApiClient from '../../services/apiClient';
import * as metadataService from '../../services/metadataService';
import { useNotification } from '../framework/NotificationContext';

/**
 * Admin review of polymer/solvent/additive names contributors proposed in bulk
 * uploads, and polymers (e.g. new copolymers) they defined in the contribution form.
 * Approve adds the name to the vocabulary; merge maps it onto an existing entry
 * (it then works as an alias); reject keeps the waiting records at "Needs update".
 * Approving or merging attaches the waiting components to their records.
 */
const errorMessage = async (response, fallback) => {
    try {
        const body = await response.json();
        if (body?.message) return body.message;
    } catch (_) { /* non-JSON body */ }
    return fallback;
};

const STATUS_FILTERS = ['PENDING', 'APPROVED', 'MERGED', 'REJECTED', 'ALL'];

export default function VocabularyProposalsPanel() {
    const { success, error: notifyError } = useNotification();
    const [status, setStatus] = useState('PENDING');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [action, setAction] = useState(null); // { type: 'approve'|'merge'|'reject', proposal }
    const [form, setForm] = useState({});
    const [existing, setExisting] = useState({ POLYMER: [], SOLVENT: [], ADDITIVE: [] });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await ApiClient.get(`/admin/vocabulary-proposals?status=${status}`);
            if (!response.ok) throw new Error(await errorMessage(response, 'Failed to load proposals'));
            setItems(await response.json());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [status]);

    const open = async (type, proposal) => {
        setAction({ type, proposal });
        setForm(type === 'approve'
            ? {
                fullName: proposal.fullName || '', casNumber: proposal.casNumber || '', abbreviation: proposal.name, category: 'OTHER',
                ...(proposal.kind === 'POLYMER' ? {
                    polymerType: proposal.polymerType || 'HOMOPOLYMER',
                    architecture: proposal.architecture || '',
                    monomers: (proposal.monomers || []).join(', '),
                } : {}),
            }
            : type === 'merge' ? { targetName: '' } : { note: '' });
        if (type === 'merge' && existing[proposal.kind].length === 0) {
            try {
                const list = proposal.kind === 'POLYMER' ? await metadataService.getAllPolymers()
                    : proposal.kind === 'ADDITIVE' ? await metadataService.getAllAdditives()
                        : await metadataService.getAllSolvents();
                const names = (list || []).map(x => x.polymerName || x.solventName || x.additiveName).filter(Boolean)
                    .sort((a, b) => a.localeCompare(b));
                setExisting(prev => ({ ...prev, [proposal.kind]: names }));
            } catch (e) {
                notifyError('Could not load the existing names.');
            }
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = action.type === 'approve' && action.proposal.kind === 'POLYMER'
                ? {
                    ...form,
                    architecture: form.polymerType === 'COPOLYMER' ? form.architecture : '',
                    monomers: form.polymerType === 'COPOLYMER'
                        ? form.monomers.split(/[,;:]/).map(m => m.trim()).filter(Boolean) : [],
                }
                : form;
            const response = await ApiClient.post(`/admin/vocabulary-proposals/${action.proposal.id}/${action.type}`, body);
            if (!response.ok) throw new Error(await errorMessage(response, 'The action failed.'));
            const result = await response.json();
            const verb = { approve: 'approved', merge: 'merged', reject: 'rejected' }[action.type];
            success(action.type === 'reject'
                ? `“${action.proposal.name}” ${verb}.`
                : `“${action.proposal.name}” ${verb}: ${result.componentsAttached} record(s) updated, ${result.recordsNowPending} back in review.`);
            setAction(null);
            load();
        } catch (err) {
            notifyError(err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <p className="text-muted small">
                Polymer, solvent and additive names contributors used in bulk uploads, and polymers (such as new copolymers) they defined in the
                contribution form, that are not in the vocabulary. Records using a pending name are
                stored as <em>Needs update</em> without that component; approving or merging attaches it and sends complete
                records back to the moderation queue.
            </p>
            <div className="d-flex flex-wrap gap-2 mb-3" role="group" aria-label="Filter proposals by status">
                {STATUS_FILTERS.map(s => (
                    <button key={s} className={`btn btn-sm ${status === s ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setStatus(s)}>
                        {s.charAt(0) + s.slice(1).toLowerCase()}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-center p-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : error ? (
                <div className="alert alert-danger">{error}</div>
            ) : (
                <div className="table-responsive">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Kind</th>
                                <th>Proposed name</th>
                                <th>Full name / CAS</th>
                                <th>Note</th>
                                <th>Proposed by</th>
                                <th style={{ textAlign: 'right' }}>Records waiting</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 ? (
                                <tr><td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>No proposals.</td></tr>
                            ) : items.map(p => (
                                <tr key={p.id}>
                                    <td>{{ POLYMER: 'Polymer', SOLVENT: 'Solvent', ADDITIVE: 'Additive' }[p.kind] || p.kind}</td>
                                    <td>
                                        <strong>{p.name}</strong>
                                        {p.polymerType === 'COPOLYMER' && (
                                            <div className="small text-muted">
                                                copolymer{p.monomers?.length ? ` of ${p.monomers.join(', ')}` : ''}
                                                {p.architecture ? ` · ${p.architecture.toLowerCase()}` : ''}
                                            </div>
                                        )}
                                        {p.polymerType === 'HOMOPOLYMER' && <div className="small text-muted">homopolymer</div>}
                                    </td>
                                    <td>{p.fullName || '—'}{p.casNumber && <div className="small text-muted">CAS {p.casNumber}</div>}</td>
                                    <td className="small">{p.note || '—'}</td>
                                    <td className="small">{p.proposedBy || '—'}{p.createdAt && <div className="text-muted">{new Date(p.createdAt).toLocaleDateString()}</div>}</td>
                                    <td style={{ textAlign: 'right' }}>{p.waitingRecords}</td>
                                    <td className="small">
                                        {p.status.charAt(0) + p.status.slice(1).toLowerCase()}
                                        {p.resolvedName && p.status === 'MERGED' && <> into <strong>{p.resolvedName}</strong></>}
                                        {p.resolutionNote && <div className="text-muted">{p.resolutionNote}</div>}
                                    </td>
                                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {(p.status === 'PENDING' || p.status === 'REJECTED') && (
                                            <>
                                                <button className="btn btn-sm btn-success me-1" onClick={() => open('approve', p)}>Approve</button>
                                                <button className="btn btn-sm btn-outline-primary me-1" onClick={() => open('merge', p)}>Merge…</button>
                                                {p.status === 'PENDING' && <button className="btn btn-sm btn-outline-danger" onClick={() => open('reject', p)}>Reject</button>}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {action && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    role="dialog" aria-modal="true" aria-labelledby="proposal-dialog-title">
                    <form onSubmit={submit} style={{ backgroundColor: '#fff', padding: 24, borderRadius: 8, width: 520, maxWidth: '92vw', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <h3 id="proposal-dialog-title" style={{ fontSize: '1.15rem', margin: 0 }}>
                            {action.type === 'approve' && <>Add “{action.proposal.name}” as a new {action.proposal.kind.toLowerCase()}</>}
                            {action.type === 'merge' && <>Merge “{action.proposal.name}” into an existing {action.proposal.kind.toLowerCase()}</>}
                            {action.type === 'reject' && <>Reject “{action.proposal.name}”</>}
                        </h3>
                        {action.type === 'approve' && (
                            <>
                                <label className="form-label small mb-0">Full name
                                    <input className="form-control" value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} />
                                </label>
                                <label className="form-label small mb-0">CAS number
                                    <input className="form-control" value={form.casNumber} onChange={e => setForm({ ...form, casNumber: e.target.value })} />
                                </label>
                                <label className="form-label small mb-0">Abbreviation
                                    <input className="form-control" value={form.abbreviation} onChange={e => setForm({ ...form, abbreviation: e.target.value })} />
                                </label>
                                {action.proposal.kind === 'POLYMER' && (
                                    <>
                                        <label className="form-label small mb-0">Type
                                            <select className="form-select" value={form.polymerType} onChange={e => setForm({ ...form, polymerType: e.target.value })}>
                                                <option value="HOMOPOLYMER">Homopolymer</option>
                                                <option value="COPOLYMER">Copolymer</option>
                                            </select>
                                        </label>
                                        {form.polymerType === 'COPOLYMER' && (
                                            <>
                                                <label className="form-label small mb-0">Monomers, in composition order (comma-separated; new ones are added to the monomer list)
                                                    <input className="form-control" value={form.monomers} placeholder="e.g. LLA, CL"
                                                        onChange={e => setForm({ ...form, monomers: e.target.value })} />
                                                </label>
                                                <label className="form-label small mb-0">Architecture
                                                    <select className="form-select" value={form.architecture} onChange={e => setForm({ ...form, architecture: e.target.value })}>
                                                        <option value="">Not stated</option>
                                                        <option value="RANDOM">Random</option>
                                                        <option value="ALTERNATING">Alternating</option>
                                                        <option value="BLOCK">Block</option>
                                                        <option value="GRAFT">Graft</option>
                                                    </select>
                                                </label>
                                            </>
                                        )}
                                    </>
                                )}
                                {action.proposal.kind === 'ADDITIVE' && (
                                    <label className="form-label small mb-0">Category
                                        <select className="form-select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                                            <option value="NANOPARTICLE">Nanoparticle</option>
                                            <option value="SALT">Salt</option>
                                            <option value="ACTIVE">Drug / active</option>
                                            <option value="FILLER">Filler</option>
                                            <option value="SURFACTANT">Surfactant</option>
                                            <option value="PLASTICIZER">Plasticiser</option>
                                            <option value="CROSSLINKER">Crosslinker</option>
                                            <option value="OTHER">Other</option>
                                        </select>
                                    </label>
                                )}
                            </>
                        )}
                        {action.type === 'merge' && (
                            <>
                                <label className="form-label small mb-0">Existing {action.proposal.kind.toLowerCase()}
                                    <select className="form-select" required value={form.targetName} onChange={e => setForm({ targetName: e.target.value })}>
                                        <option value="">Choose…</option>
                                        {existing[action.proposal.kind].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </label>
                                <div className="small text-muted">“{action.proposal.name}” will be recognised as this name in future uploads.</div>
                            </>
                        )}
                        {action.type === 'reject' && (
                            <label className="form-label small mb-0">Reason (shown to contributors who use the name)
                                <textarea className="form-control" required rows={3} value={form.note} onChange={e => setForm({ note: e.target.value })} />
                            </label>
                        )}
                        <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-light" onClick={() => setAction(null)} disabled={busy}>Cancel</button>
                            <button type="submit" className={`btn ${action.type === 'reject' ? 'btn-danger' : 'btn-primary'}`} disabled={busy}>
                                {busy ? 'Working…' : action.type === 'approve' ? 'Approve' : action.type === 'merge' ? 'Merge' : 'Reject'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
